/**
 * UNA TARJETA DE LA ACTIVIDAD: un partido que ya pasó, contado como novedad.
 *
 * NO ES `WallCard`, Y SON DOS DIBUJOS A PROPÓSITO. `WallCard` es la agenda:
 * dos nombres enfrentados, la ronda y cuándo se juega, porque lo que se lee
 * ahí es qué falta. Acá lo que se lee es CÓMO TERMINÓ, y eso se lee en filas:
 * cada quien con su categoría, su ✓ o su ✕, lo que sumó y sus games al lado.
 * Es la misma forma del editor de marcador —los dos nombres como filas, los
 * sets como columnas— y no es casualidad: quien anota un resultado y quien
 * después lo lee están mirando la misma tabla.
 *
 * ADEMÁS UN RETO NO TIENE RONDA NI CUADRO, así que la cabecera de `WallCard`
 * —«Cuartos de final · B · Masculino»— no se puede escribir para la mitad de
 * lo que entra acá.
 *
 * LOS PUNTOS SALEN DEL SERVIDOR YA RESUELTOS, uno por lado. Esta pantalla no
 * sabe cuánto vale ganarle a alguien con mil puntos, y el día que eso cambie
 * no tiene por qué enterarse.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type {
  ActivityEntry,
  ActivitySide,
  MatchComment,
  MatchScore,
} from '@tennisup/contracts';

import { Avatar } from './Avatar';
import { CommentThread, Reactions } from './Conversation';
import { ScoreEditor } from './ScoreEditor';
import { assetUrl } from '../lib/api';
import { rutaDelPartido } from '../lib/activityActions';

/**
 * UN LADO DE LA TARJETA, con la forma que pide `ScoreEditor`.
 *
 * `userId` es `null` cuando jugó un invitado —alguien sin cuenta—, y ahí no
 * hay a quién declarar ganador: se devuelve `null` y el editor dibuja ese
 * lado vacío en vez de inventar un id.
 */
function ladoParaEditor(lado: ActivitySide): { id: string; name: string; avatarPath: string | null } | null {
  if (lado.userId === null) return null;
  return { id: lado.userId, name: lado.displayName, avatarPath: lado.avatarPath };
}

export function ActivityCard({
  entrada,
  yoId,
  onComment,
  onDeleteComment,
  onReact,
  onFixScore,
  permalink = true,
}: {
  entrada: ActivityEntry;
  /** Para poder decir «este soy yo» sin volver a preguntarle al servidor. */
  yoId: string;
  onComment: (entrada: ActivityEntry, body: string) => Promise<void>;
  onDeleteComment: (comentario: MatchComment) => Promise<void>;
  onReact: (entrada: ActivityEntry, emoji: string, on: boolean) => Promise<void>;
  /**
   * CORREGIR EL MARCADOR. Sin esto, la tarjeta no ofrece corregir nada.
   *
   * QUIÉN PUEDE LO DICE `entrada.canFixScore`, que lo contesta el servidor
   * con la misma regla que aplica al guardar. Esta pantalla no lo deduce:
   * deducirlo sería un botón que se inventa un permiso, o sea un 403 con
   * forma de botón.
   */
  onFixScore?: (
    entrada: ActivityEntry,
    score: MatchScore,
    winnerUserId: string | null,
    superTieBreak: boolean,
  ) => Promise<void>;
  /** Si la fecha lleva a la pantalla de este partido. Apagado cuando ya es. */
  permalink?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [home, away] = entrada.sides;
  const [corrigiendo, setCorrigiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);

  /*
   * CONTRA QUIÉN ABRIR EL CARA A CARA, cuando yo soy uno de los dos.
   *
   * El «vs 3» sale de MI historial —entre dos terceros el servidor manda
   * cero— así que el otro es el que no soy yo.
   */
  const elOtro = home.userId === yoId ? away.userId : home.userId;

  /*
   * Y SI NO SOY NINGUNO, EL CARA A CARA DE ELLOS DOS.
   *
   * Javier: «Cuando abro esta vista deberia poder ver el cara a cara de ambos
   * jugadores». Antes no había forma de llegar: el enlace dependía del «vs 3»,
   * que entre dos terceros vale cero.
   *
   * VA SIN NÚMERO, a propósito. Contar los enfrentamientos de cada par de una
   * página entera serían decenas de consultas por cada scroll, para un número
   * que casi nadie mira. El enlace lleva a la pantalla que sí hace esa cuenta,
   * para el par que de verdad interesó — y ahí el número sale completo.
   *
   * LOS DOS TIENEN QUE TENER CUENTA: contra un invitado no hay historial que
   * consultar. Ver `vecesEntreLosDos` en el servidor, misma razón.
   */
  const juegoYo = home.userId === yoId || away.userId === yoId;
  const paraEllosDos =
    !juegoYo && home.userId !== null && away.userId !== null
      ? { a: home.userId, b: away.userId }
      : null;

  return (
    <article className="act-card">
      <header className="act-head">
        {/*
          LA FECHA ES EL ENLACE A ESTE PARTIDO SOLO.
          Cada tarjeta tiene su dirección, y esta es la forma de copiarla o de
          abrirla en otra pestaña. Va en la fecha y no en un botón «ver»
          aparte: la fecha ya está ahí, no dice nada más que lo que dice, y no
          le saca lugar a nada. Cuando la tarjeta YA ES la pantalla del
          partido, `permalink` va apagado: un enlace a donde uno ya está es un
          enlace que no hace nada.
        */}
        {permalink ? (
          <Link className="act-when" to={rutaDelPartido(entrada.kind, entrada.id)}>
            {formatDay(entrada.playedOn)}
          </Link>
        ) : (
          <span className="act-when">{formatDay(entrada.playedOn)}</span>
        )}
        <span className="act-tags">
          <Etiqueta entrada={entrada} />
          {entrada.communityId !== null && entrada.communityName !== null && (
            <Link className="tag act-club" to={`/clubes/${entrada.communityId}`}>
              {entrada.communityName}
            </Link>
          )}
          {entrada.level !== null && (
            <span className="tag act-cat">
              {t(`categoryLevel.${entrada.level}`)}
              {entrada.sex !== null && ` · ${t(`sex.${entrada.sex}`)}`}
            </span>
          )}
        </span>
      </header>

      {/* LA TABLA: una fila por persona, una columna por set. */}
      {/*
        LA INSIGNIA DE CATEGORÍA, SOLO CUANDO LOS DOS NO SON DE LA MISMA.
        Casi siempre lo son —un cuadro es de una categoría, y un reto también—
        y entonces la insignia repite lo que ya dice la cabecera, a cambio de
        casi treinta píxeles que le hacen falta al nombre. Cuando difieren
        —un torneo abierto donde alguien juega una categoría que no es la
        suya— es justo el dato que explica la tarjeta, y ahí sí se dibuja.
      */}
      <div className="act-table">
        <Fila
          lado={home}
          sets={entrada.score.map((s) => s.home)}
          walkover={entrada.walkover}
          conNivel={home.level !== away.level}
        />
        <Fila
          lado={away}
          sets={entrada.score.map((s) => s.away)}
          walkover={entrada.walkover}
          conNivel={home.level !== away.level}
        />
      </div>

      {/* EL «vs 3» CUANDO YO SOY UNO DE LOS DOS: ver `elOtro`. */}
      {entrada.playedTogether > 0 && elOtro !== null && (
        <p className="act-vs">
          <Link className="tag tag-vs" to={`/jugadores/${elOtro}/vs`}>
            {t('challenges.playedTogether', { count: entrada.playedTogether })}
          </Link>
        </p>
      )}

      {/* Y EL DE ELLOS DOS CUANDO NO SOY NINGUNO: ver `paraEllosDos`. */}
      {paraEllosDos !== null && (
        <p className="act-vs">
          <Link
            className="tag tag-vs"
            to={`/jugadores/${paraEllosDos.a}/vs/${paraEllosDos.b}`}
          >
            {/*
              «vs» A SECAS EN LOS PARTIDOS AJENOS, y no «vs 0».
              Javier pidió «poner las veces que se han enfrentado», y acá no
              se pueden: el servidor solo cuenta los enfrentamientos de QUIEN
              MIRA (`vecesConEsaGente` en `wall.service`), así que entre dos
              terceros manda cero — que NO significa «nunca jugaron». Poner
              «vs 0» sería afirmar algo falso; «vs» invita a entrar y verlo.
              Que ese número exista es un cambio del backend, aparte.
            */}
            {t('rivalry.between')}
          </Link>
        </p>
      )}

      {entrada.photoPath !== null && (
        <p className="act-photo">
          <img
            src={assetUrl(entrada.photoPath) ?? ''}
            alt={t('score.photoOf', { home: home.displayName, away: away.displayName })}
            loading="lazy"
          />
        </p>
      )}

      {/*
        CORREGIR EL MARCADOR, DESDE ACÁ.
 
        POR QUÉ ACÁ Y NO SOLO EN «MIS RETOS». El botón vivía solo allá, y
        «Mis retos» trae —como su nombre dice— los retos PROPIOS: un admin de
        plataforma corrigiendo el partido de otros dos nunca lo iba a
        encontrar. Javier lo pidió así y así quedó escrito en la regla; lo que
        faltaba era el camino para usarlo.

        EL AVISO DE QUE YA ESTABA ANOTADO va siempre, porque acá SIEMPRE lo
        está: esta tarjeta solo existe para partidos con resultado. Javier:
        «si trata de caer encima mencionar que ha sido reportado y si esta de
        acuerdo volver a editarlo».
      */}
      {entrada.canFixScore && onFixScore !== undefined && !corrigiendo && (
        <p className="act-vs">
          <button type="button" className="link" onClick={() => setCorrigiendo(true)}>
            {t('challenge.fixScore')}
          </button>
        </p>
      )}

      {corrigiendo && onFixScore !== undefined && (
        <ScoreEditor
          home={ladoParaEditor(home)}
          away={ladoParaEditor(away)}
          score={entrada.score}
          noShowId={null}
          photoKey={entrada.id}
          /* Un amistoso puede quedar empatado; un reto por ranking no. */
          allowDraw={entrada.kind === 'CHALLENGE_FRIENDLY'}
          /* También acá: corregir un marcador puede ser justamente arreglar
             el check que se marcó sin querer. Ver `ScoreEditor`. */
          askSuperTieBreak
          superTieBreak={entrada.superTieBreak}
          onSave={async (score, _noShowId, winner, superTieBreak) => {
            if (guardando) return;
            setGuardando(true);
            try {
              /*
               * EL EDITOR CONTESTA `'home' | 'away' | null` —de qué LADO ganó—
               * y el servidor quiere el id de quién. La traducción va acá, que
               * es donde se sabe quién está en cada lado; `null` es empate y
               * viaja tal cual.
               */
              const winnerUserId =
                winner === null ? null : winner === 'home' ? home.userId : away.userId;
              await onFixScore(entrada, score, winnerUserId, superTieBreak);
              setCorrigiendo(false);
            } finally {
              setGuardando(false);
            }
          }}
          onCancel={() => setCorrigiendo(false)}
        />
      )}

      <Reactions
        reactions={entrada.reactions}
        onToggle={(emoji, on) => void onReact(entrada, emoji, on)}
      />
      <CommentThread
        id={entrada.id}
        comments={entrada.comments}
        onComment={(body) => onComment(entrada, body)}
        onDelete={onDeleteComment}
      />
    </article>
  );
}

/**
 * DE QUÉ ES ESTE PARTIDO: el torneo, o que fue un reto.
 *
 * El torneo va con enlace y el reto no: un reto no tiene pantalla propia a la
 * que llevar. Un enlace que no lleva a ningún lado es peor que no tenerlo.
 */
function Etiqueta({ entrada }: { entrada: ActivityEntry }): JSX.Element {
  const { t } = useTranslation();

  if (entrada.kind === 'TOURNAMENT' && entrada.tournamentId !== null) {
    return (
      <Link className="tag act-tournament" to={`/torneos/${entrada.tournamentId}`}>
        {entrada.tournamentName ?? ''}
      </Link>
    );
  }
  return (
    <span className="tag act-kind">
      {t(entrada.kind === 'CHALLENGE_FRIENDLY' ? 'challenge.friendlyTag' : 'activity.rankedTag')}
    </span>
  );
}

/**
 * UNA FILA: quién, cómo le fue, cuánto sumó, y sus games.
 *
 * LOS GAMES VAN EN CELDAS SUELTAS y no en «6-4, 7-5»: puestos uno debajo del
 * otro, los dos números de un set quedan en la misma columna y se leen de un
 * vistazo. Con la cadena hay que contar comas para saber quién ganó el tercero.
 */
function Fila({
  lado,
  sets,
  walkover,
  conNivel,
}: {
  lado: ActivitySide;
  sets: readonly number[];
  walkover: boolean;
  /** Si la insignia de categoría dice algo. Ver la tarjeta. */
  conNivel: boolean;
}): JSX.Element {
  const { t } = useTranslation();

  const dentro = (
    <>
      {/* 32 y no 44: en una fila de cuatro columnas, cada píxel de foto es un
          píxel menos de nombre. Ver el porqué en `.act-row`. */}
      <Avatar url={assetUrl(lado.avatarPath)} name={lado.displayName} size={32} />
      <span className="act-name">{lado.displayName}</span>
      {conNivel && lado.level !== null && (
        <span className="act-level" aria-label={t(`categoryLevel.${lado.level}`)}>
          {t(`categoryLevel.${lado.level}`)}
        </span>
      )}
    </>
  );

  return (
    <div className={lado.outcome === 'WON' ? 'act-row act-row-won' : 'act-row'}>
      {/*
        UN INVITADO NO TIENE FICHA QUE ABRIR: no tiene cuenta, así que
        `/jugadores/null` sería una pantalla de error con el nombre de una
        persona real encima. Se dibuja igual, pero sin enlace.
      */}
      {lado.userId === null ? (
        <span className="act-who">{dentro}</span>
      ) : (
        <Link className="act-who" to={`/jugadores/${lado.userId}`}>
          {dentro}
        </Link>
      )}

      {/*
        EL ✓, LOS PUNTOS Y LOS GAMES VIAJAN JUNTOS EN UNA CAJA.
        En un teléfono no caben en el mismo renglón que un nombre largo, y
        agrupados pueden bajar a un segundo renglón SIN romper la tabla: los
        tres siguen teniendo el mismo ancho en las dos filas, así que el ✓ de
        arriba sigue justo encima del ✕ de abajo. Ver `.act-result`.
      */}
      <span className="act-result">
      <Marca outcome={lado.outcome} />

      {/*
        `null` Y NO CERO: un cero al lado de un amistoso invita a preguntar
        por qué no dio ninguno, cuando la respuesta es que nunca iba a dar.

        SE DIBUJA «+25» Y LA PALABRA VA EN EL NOMBRE ACCESIBLE. En la fila hay
        cuatro columnas y un teléfono mide 390 px: «+25 puntos» empuja los
        games fuera de la pantalla. El número no necesita traducción; quien
        usa lector de pantalla oye «25 puntos» igual.
      */}
      <span
        className="act-points"
        {...(lado.points === null
          ? {}
          : { 'aria-label': t('challenge.points', { count: lado.points }) })}
      >
        {lado.points === null ? '' : `+${lado.points}`}
      </span>

      <span className="act-sets">
        {walkover || sets.length === 0 ? (
          <span className="act-wo">{t('score.wo')}</span>
        ) : (
          sets.map((games, i) => (
            // El índice ES la identidad: un set es «el tercero», no una cosa
            // con nombre propio, y la lista no se reordena nunca.
            // eslint-disable-next-line react/no-array-index-key
            <span key={i} className="act-set">
              {games}
            </span>
          ))
        )}
      </span>
      </span>
    </div>
  );
}

/** ✓, ✕ o =. Con nombre para quien no ve el símbolo. */
function Marca({ outcome }: { outcome: ActivitySide['outcome'] }): JSX.Element {
  const { t } = useTranslation();
  const qué =
    outcome === 'WON'
      ? { clase: 'act-mark act-mark-won', signo: '✓', texto: t('challenge.won') }
      : outcome === 'DRAW'
        ? { clase: 'act-mark act-mark-draw', signo: '=', texto: t('challenge.drew') }
        : { clase: 'act-mark act-mark-lost', signo: '✕', texto: t('challenge.lost') };

  return (
    <span className={qué.clase}>
      <span aria-hidden="true">{qué.signo}</span>
      <span className="visually-hidden">{qué.texto}</span>
    </span>
  );
}

/** `2026-10-11` → `11/10/2026`. Fecha civil, sin zona horaria. */
function formatDay(iso: string): string {
  const [year, month, day] = iso.split('-');
  return year === undefined || month === undefined || day === undefined
    ? iso
    : `${day}/${month}/${year}`;
}
